import { Component, OnInit, ElementRef, Inject, ViewChild, ChangeDetectorRef, ViewRef } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';

import jspdf from 'jspdf';
import html2canvas from 'html2canvas';

import { FormsService } from '../../forms.service';
import { FormsGlobals } from '../../../../models/forms/forms.enum';
import { FormConfigurationModel } from '../../../../models/forms/form-configuration.model';
import { FormVersionConfigurationModel } from '../../../../models/forms/form-version-configuration.model';

interface PrintFormEntry {
  formConfig: FormConfigurationModel;
  cfgFormVersion: FormVersionConfigurationModel;
  formName: string;
  pageBreakAfterIndices: Set<number>;
}

@Component({
  standalone: false,
  selector: 'app-print-preview',
  templateUrl: './print-preview.component.html',
  styleUrls: ['./print-preview.component.scss']
})
export class PrintPreviewComponent implements OnInit {
  @ViewChild('printContainer') printContainerRef: ElementRef;

  formEntries: PrintFormEntry[] = [];
  isLoading: boolean = true;
  isGeneratingPdf: boolean = false;
  loadProgress: number = 0;

  constructor(
    public dialogRef: MatDialogRef<PrintPreviewComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { formIds: string[] },
    private formsService: FormsService,
    private globals: FormsGlobals,
    private snackBar: MatSnackBar,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.loadAllForms();
  }

  async loadAllForms(): Promise<void> {
    this.isLoading = true;
    this.loadProgress = 0;

    try {
      const total = this.data.formIds.length;
      let loaded = 0;

      for (const formId of this.data.formIds) {
        const formConfig = await this.formsService.getCfgDForm(formId).toPromise();

        if (!formConfig) {
          loaded++;
          this.loadProgress = Math.round((loaded / total) * 100);
          if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
          continue;
        }

        const version = this.getActiveVersion(formConfig);
        if (!version) {
          loaded++;
          this.loadProgress = Math.round((loaded / total) * 100);
          if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
          continue;
        }

        // Filter deleted paragraphs and items, sort by ParagraphOrder
        version.cfgDFormParagraphs = (version.cfgDFormParagraphs || [])
          .filter(p => !p.xauditDeleted)
          .sort((a, b) => a.ParagraphOrder - b.ParagraphOrder);
        version.cfgDFormParagraphs.forEach(p => {
          p.cfgDFormItems = (p.cfgDFormItems || []).filter(i => !i.xauditDeleted);
        });

        // Track which paragraphs have PageBreakAfter, then clear the flag
        // so app-forms-form renders flat (no accordion sections)
        const pageBreakAfterIndices = new Set<number>();
        version.cfgDFormParagraphs.forEach((p, idx) => {
          if (p.PageBreakAfter) pageBreakAfterIndices.add(idx);
          p.PageBreakAfter = false;
        });

        this.formEntries.push({
          formConfig,
          cfgFormVersion: version,
          formName: formConfig.DFormName || 'Untitled',
          pageBreakAfterIndices
        });

        loaded++;
        this.loadProgress = Math.round((loaded / total) * 100);
        if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
      }
    } catch (err) {
      console.error('Error loading forms for print:', err);
      this.snackBar.open('Error loading forms. Please try again.', 'Error', {
        duration: 5000,
        verticalPosition: 'top'
      });
    } finally {
      this.isLoading = false;
      if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
    }
  }

  getActiveVersion(formConfig: FormConfigurationModel): FormVersionConfigurationModel | null {
    if (!formConfig.cfgDFormVersions || formConfig.cfgDFormVersions.length === 0) {
      return null;
    }

    const nonDeleted = formConfig.cfgDFormVersions.filter(v => !v.xauditDeleted);
    if (nonDeleted.length === 0) return null;

    // Find highest approved version
    const approved = nonDeleted
      .filter(v => v.enumDFormBuildStatusID?.toLowerCase() === this.globals.FORM_STATUS_APPROVED?.toLowerCase());

    if (approved.length > 0) {
      return JSON.parse(JSON.stringify(
        approved.reduce((max, v) => v.Version > max.Version ? v : max)
      ));
    }

    // No approved versions â€” return highest version number
    return JSON.parse(JSON.stringify(
      nonDeleted.reduce((max, v) => v.Version > max.Version ? v : max)
    ));
  }

  async generatePdf(): Promise<void> {
    if (this.formEntries.length === 0) return;

    this.isGeneratingPdf = true;

    // Wait for forms to fully render and settle before canvas capture
    await this.waitForStablePrintLayout();

    const MARGIN = 10;                       // mm margin on each side
    const PAGE_WIDTH = 210 - (MARGIN * 2);   // A4 usable width in mm  (190)
    const PAGE_HEIGHT = 297 - (MARGIN * 2);  // A4 usable height in mm (277)
    const SCALE = 1.5;                       // html2canvas scale â€” balances quality vs file size

    try {
      const doc = new jspdf('p', 'mm');
      const formContainers = this.printContainerRef.nativeElement
        .querySelectorAll('.print-form-entry');

      let isFirstForm = true;

      for (let formIdx = 0; formIdx < formContainers.length; formIdx++) {
        const container = formContainers[formIdx] as HTMLElement;
        const entry = this.formEntries[formIdx];

        // Each form starts on a new page (first form uses the page jsPDF creates automatically)
        if (!isFirstForm) {
          doc.addPage();
        }
        isFirstForm = false;

        // Capture the entire form as one canvas â€” one html2canvas call per form
        const canvas = await this.captureFormCanvas(container, SCALE);

        // Convert PAGE_HEIGHT from mm to canvas pixels for cut-point calculation
        const pageHeightPx = (PAGE_HEIGHT / PAGE_WIDTH) * canvas.width;

        // Find safe Y positions (between paragraphs) where new pages begin
        const cutPoints = this.getSafeCutPoints(container, canvas, pageHeightPx, entry);

        // Slice the canvas and add each strip as a page image
        this.addCanvasSlicesToDoc(doc, canvas, cutPoints, MARGIN, PAGE_WIDTH, PAGE_HEIGHT);
      }

      const timestamp = new Date().toISOString().slice(0, 10);
      const fileName = this.formEntries.length === 1
        ? `${this.formEntries[0].formName}.pdf`
        : `Forms-Export-${timestamp}.pdf`;

      doc.save(fileName);

      this.snackBar.open('PDF saved successfully', 'OK', {
        duration: 3000,
        verticalPosition: 'top'
      });
    } catch (err) {
      console.error('PDF generation error:', err);
      this.snackBar.open('Error generating PDF. Please try again.', 'Error', {
        duration: 5000,
        verticalPosition: 'top'
      });
    } finally {
      this.isGeneratingPdf = false;
      if (!(this.cdr as ViewRef).destroyed) { this.cdr.detectChanges(); }
    }
  }

  /**
   * Captures an entire form container as a single canvas.
   * One html2canvas call per form instead of one per element.
   */
  private async captureFormCanvas(element: HTMLElement, scale: number): Promise<HTMLCanvasElement> {
    return html2canvas(element, {
      useCORS: true,
      logging: false,
      scale,
      onclone: (clonedDoc: Document, clonedEl: HTMLElement) => {
        const styleEl = clonedDoc.createElement('style');
        styleEl.textContent = `
          *,
          *::before,
          *::after {
            animation: none !important;
            transition: none !important;
          }
          .radio-control-container { margin-top: 0 !important; }
          .mat-mdc-form-field-icon-suffix { position: relative !important; top: -8px !important; }
          app-forms-item .main-container { width: 100% !important; max-width: 100% !important; }
          .mdc-label { white-space: normal !important; line-height: 1.4 !important; overflow-wrap: anywhere !important; }
        `;
        (clonedDoc.head || clonedDoc.body).appendChild(styleEl);

        clonedEl.querySelectorAll('input, textarea').forEach((el: Element) => {
          el.removeAttribute('placeholder');
        });
        clonedEl.querySelectorAll('.signature-placeholder').forEach((el: Element) => {
          el.remove();
        });
        clonedEl.querySelectorAll('[data-cy="forms-item-signature-sign-name-button"]').forEach((el: Element) => {
          el.remove();
        });
      }
    });
  }

  /**
   * Returns an array of canvas-pixel Y positions where a new PDF page should begin.
   * Cut points are placed at safe boundaries (between paragraphs or between rows within
   * a paragraph) so content is never split mid-item.
   *
   * When a paragraph fits on the current page: leave it there.
   * When a paragraph would overflow but fits entirely on the next page: cut before it.
   * When a paragraph is taller than a full page (e.g. 11 questions in one paragraph):
   *   fall back to row-level cuts within that paragraph so nothing disappears.
   *
   * Coordinates use getBoundingClientRect() + container.scrollTop, which converts
   * viewport-relative rects into content-relative positions regardless of scroll state
   * or CSS positioning on the container element.
   */
  private getSafeCutPoints(
    container: HTMLElement,
    canvas: HTMLCanvasElement,
    pageHeightPx: number,
    entry: PrintFormEntry
  ): number[] {
    // html2canvas captures the full scroll height, so scale against that
    const scaleY = canvas.height / container.scrollHeight;

    const paragraphEls = Array.from(
      container.querySelectorAll('app-forms-paragraph')
    ) as HTMLElement[];

    // containerRect.top is the viewport-origin of the container's top edge.
    // Adding container.scrollTop converts any element's viewport-rect.top into
    // a content-relative Y (what the element's top is within the full scrollable content).
    const containerRect = container.getBoundingClientRect();

    const cutPoints: number[] = [];
    let pageStartPx = 0;

    paragraphEls.forEach((paraEl, pIdx) => {
      const paraRect = paraEl.getBoundingClientRect();
      const paraTopLayout    = (paraRect.top  - containerRect.top) + container.scrollTop;
      const paraBottomLayout = paraTopLayout + paraEl.offsetHeight;

      const paraTopPx    = paraTopLayout    * scaleY;
      const paraBottomPx = paraBottomLayout * scaleY;

      const paragraphHeight = paraBottomPx - paraTopPx;

      if (paragraphHeight > pageHeightPx) {
        // Paragraph is taller than one full page â€” must cut within it at row boundaries.
        // First: if the paragraph doesn't start at the top of the current page and some
        // content has already been placed, cut before this paragraph so it starts fresh.
        if (paraTopPx > pageStartPx) {
          cutPoints.push(paraTopPx);
          pageStartPx = paraTopPx;
        }

        // Walk each .forms-paragraph-row and cut between rows whenever the next row
        // would overflow the current page.
        const rowEls = Array.from(
          paraEl.querySelectorAll('.forms-paragraph-row')
        ) as HTMLElement[];

        rowEls.forEach(rowEl => {
          const rowRect = rowEl.getBoundingClientRect();
          const rowTopLayout    = (rowRect.top    - containerRect.top) + container.scrollTop;
          const rowBottomLayout = rowTopLayout + rowEl.offsetHeight;

          const rowTopPx    = rowTopLayout    * scaleY;
          const rowBottomPx = rowBottomLayout * scaleY;

          if (rowBottomPx > pageStartPx + pageHeightPx) {
            // This row overflows â€” cut at its top to start a fresh page
            cutPoints.push(rowTopPx);
            pageStartPx = rowTopPx;
          }
        });

      } else if (paraBottomPx > pageStartPx + pageHeightPx) {
        // Paragraph fits on a single page but overflows the current one â€” cut before it
        cutPoints.push(paraTopPx);
        pageStartPx = paraTopPx;
      }
      // Paragraph fits on the current page â€” no action needed

      // Honor PageBreakAfter: force a new page immediately after this paragraph
      if (entry.pageBreakAfterIndices.has(pIdx) && pIdx < paragraphEls.length - 1) {
        cutPoints.push(paraBottomPx);
        pageStartPx = paraBottomPx;
      }
    });

    return cutPoints;
  }

  /**
   * Slices the full-form canvas at the given cut points and adds each strip as a
   * page image in the PDF document. The first strip lands on the already-current page.
   */
  private addCanvasSlicesToDoc(
    doc: jspdf,
    canvas: HTMLCanvasElement,
    cutPoints: number[],
    margin: number,
    pageWidthMm: number,
    _pageHeightMm: number
  ): void {
    const sliceBoundaries = [0, ...cutPoints, canvas.height];

    const tmpCanvas = document.createElement('canvas');
    tmpCanvas.width = canvas.width;
    const ctx = tmpCanvas.getContext('2d');

    for (let i = 0; i + 1 < sliceBoundaries.length; i++) {
      const startY = Math.floor(sliceBoundaries[i]);
      const endY = Math.floor(sliceBoundaries[i + 1]);
      const sliceH = endY - startY;
      if (sliceH <= 0) continue;

      // New page for every slice after the first
      if (i > 0) {
        doc.addPage();
      }

      tmpCanvas.height = sliceH;
      ctx.clearRect(0, 0, tmpCanvas.width, sliceH);
      ctx.drawImage(canvas, 0, startY, canvas.width, sliceH, 0, 0, canvas.width, sliceH);

      const imgData = tmpCanvas.toDataURL('image/jpeg', 0.88);
      const sliceHeightMm = (sliceH / canvas.width) * pageWidthMm;

      doc.addImage(imgData, 'JPEG', margin, margin, pageWidthMm, sliceHeightMm);
    }
  }

  private async waitForStablePrintLayout(): Promise<void> {
    await this.waitForAnimationFrames(2);

    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    if (fonts?.ready) {
      try {
        await Promise.race([fonts.ready, this.delay(3000)]);
      } catch {
        // Continue even if font readiness check fails.
      }
    }

    if (!this.printContainerRef?.nativeElement) {
      await this.waitForAnimationFrames(1);
      return;
    }

    const container = this.printContainerRef.nativeElement as HTMLElement;
    const images = Array.from(container.querySelectorAll('img')) as HTMLImageElement[];
    await Promise.all(images.map(image => this.waitForImageLoad(image)));

    await this.waitForLayoutStability(container);
    await this.waitForAnimationFrames(1);
  }

  private waitForImageLoad(image: HTMLImageElement): Promise<void> {
    if (image.complete) {
      return Promise.resolve();
    }

    return new Promise(resolve => {
      const onDone = () => {
        image.removeEventListener('load', onDone);
        image.removeEventListener('error', onDone);
        resolve();
      };

      image.addEventListener('load', onDone);
      image.addEventListener('error', onDone);
    });
  }

  private async waitForLayoutStability(container: HTMLElement): Promise<void> {
    let lastHeight = container.scrollHeight;
    let stableCycles = 0;
    const maxCycles = 20;

    for (let i = 0; i < maxCycles && stableCycles < 3; i++) {
      await this.delay(50);
      const nextHeight = container.scrollHeight;

      if (nextHeight === lastHeight) {
        stableCycles++;
      } else {
        stableCycles = 0;
        lastHeight = nextHeight;
      }
    }
  }

  private waitForAnimationFrames(count: number): Promise<void> {
    return new Promise(resolve => {
      const step = (remaining: number) => {
        if (remaining <= 0) {
          resolve();
          return;
        }

        requestAnimationFrame(() => step(remaining - 1));
      };

      step(count);
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  removeForm(index: number): void {
    this.formEntries.splice(index, 1);
  }

  close(): void {
    this.dialogRef.close();
  }
}

